import { Injectable } from '@angular/core';
import {LocalizeTaggedTemplateEntry} from "./localize-tagged-template-entry";
import * as ts from "typescript";
import {
  Identifier,
  NoSubstitutionTemplateLiteral, SourceFile,
  TaggedTemplateExpression,
  TemplateExpression,
  TemplateMiddle, TemplateTail, TransformationContext
} from "typescript";

// NOTE: these are internal symbols of Angular and may change without notice.
import {ɵmakeTemplateObject, ɵParsedMessage, ɵparseMessage, ɵSourceLocation} from "@angular/localize";
import {cloneDeep} from 'lodash';
import {InternationalizationService} from "./internationalization.service";

declare interface LocalizeTaggedTemplateExtract {
  literals: string[],
  raw: string[],
  expressions: string[]
}

const $localizeOrig = (window as any).$localize;

export function $translate(messageParts: TemplateStringsArray, ...expressions: any): string;
export function $translate(key: string): (messageParts: TemplateStringsArray, ...expressions: any) => string;

export function $translate(messageParts: any, ...expressions: any): unknown {

  if (Array.isArray(messageParts) && (messageParts as any).raw) {
    // This is a template literal. Treat it the same as $localize
    return $localizeOrig(messageParts as unknown as TemplateStringsArray, ...expressions);
  } else if (typeof messageParts === 'string' && expressions.length === 0) {
    // This is a single parameter which is a key. Return a closure that calls $localize
    const key = messageParts;
    return (messageParts: TemplateStringsArray, ...expressions: any) => {
      let clonedMessageParts = cloneDeep(messageParts);
      clonedMessageParts.raw = cloneDeep(messageParts.raw);
      clonedMessageParts[0] = `:@@${key}:${clonedMessageParts[0]}`;
      clonedMessageParts.raw[0] = `:@@${key}:${clonedMessageParts.raw[0]}`;
      return $localizeOrig(clonedMessageParts, ...expressions);
    };
  }
}

export function $translateKey(key: string): string {
  const messageParts = [`:@@${key}:`];
  (messageParts as any).raw = messageParts;
  return $localizeOrig(messageParts);
}

// If we redefine $localize we can use both overloads, however cli extraction of the messages fails
//
// export function $localize(messageParts: TemplateStringsArray, ...expressions: any): string;
// export function $localize(key: string): (messageParts: TemplateStringsArray, ...expressions: any) => string;
//
// export function $localize(messageParts: any, ...expressions: any): unknown {
//
//   if (Array.isArray(messageParts) && (messageParts as any).raw) {
//     // This is a template literal. Treat it the same as $localize
//     // We lie with the type signature to keep the compiler happy.
//     return $localizeOrig(messageParts as unknown as TemplateStringsArray, ...expressions);
//   } else if (typeof messageParts === 'string' && expressions.length === 0) {
//     // This is a single parameter which is a key. Return a closure that calls $localize
//     const key = messageParts;
//     return (messageParts: TemplateStringsArray, ...expressions: any) => {
//       let clonedMessageParts = cloneDeep(messageParts);
//       clonedMessageParts.raw = cloneDeep(messageParts.raw);
//       clonedMessageParts[0] = `:@@${key}:${clonedMessageParts[0]}`;
//       clonedMessageParts.raw[0] = `:@@${key}:${clonedMessageParts.raw[0]}`;
//       return $localizeOrig(clonedMessageParts,  ...expressions);
//     };
//   }
// }
//
// (window as any).$localize = $translate;

@Injectable({
  providedIn: 'root'
})
export class LocalizeService {

  NG_PREFIX = "ng";

  constructor() {
    (window as any).NG_SHARED_LOCALIZE_SERVICE = this;
  }

  init(locale?: string) {
    // Use our translation mechanism instead of the default one
    $localizeOrig.translate = this.translate.bind(this);
    (window as any).$translate = $translate;
  }

  translate(literals: TemplateStringsArray, expressions: any[]): [TemplateStringsArray, any[]] {

    // Done this way to break a circular dependency.
    const i18nService: InternationalizationService = (window as any).NG_SHARED_INTERNATIONALIZATION_SERVICE;

    let extract = {
      literals: literals as unknown as string[],
      raw: literals.raw as string[],
      expressions: expressions
    };
    let tt = this.processTaggedTemplate(extract, "");

    // Default if we don't find the key by lookup
    let text = tt.literals[0];
    for (let i = 1; i < tt.literals.length; ++i) {
      text += tt.expressions[i - 1] + tt.literals[i];
    }

    if (tt.id) {
        if (i18nService.hasMessage(tt.id)) {
          // We modify the data to be keys by idx, as the messages in ICU format will have {0} etc
          const mappedExpressions = tt.expressions.reduce((acc, curr, idx) => {
            acc[String(idx)] = curr;
            return acc;
          }, {});
            text = i18nService.getMessage(tt.id, mappedExpressions);
        } else {
          // We have an id but no message
          if (!text) {
            // We have an id but no default so just send back the key
            text = tt.id;
          }
        }
    }

    // We have already generated the complete message so we just send it back as a single string
    let parts = [text];
    let messageParts = ɵmakeTemplateObject(parts, parts);
    return [messageParts, []];
  }

  extractLocalizeTaggedTemplates(input: string | string[], prefix: string = "", tableClass: string = "", recordId: string = "", fieldName: string = ""): LocalizeTaggedTemplateEntry[] {
    const localizeTaggedTemplates: LocalizeTaggedTemplateExtract[] = [];

    const inputs = Array.isArray(input) ? input : [input];

    inputs
      .filter(input => input != null)
      .forEach(input => {
        ts.transpileModule(input, {
          compilerOptions: {target: ts.ScriptTarget.ES2015, emitDecoratorMetadata: true},
          transformers: {
            before: [(context: TransformationContext) => {
              return (node: SourceFile) => {
                this._extractLocalizeTaggedTemplates(node, localizeTaggedTemplates);
                return node;
              }
            }],
            after: [],
          }
        });
    });

    const idPrefix = [this.NG_PREFIX, prefix, tableClass, recordId, fieldName].filter(s => !!s).join(".");

    const processedTaggedTemplates: LocalizeTaggedTemplateEntry[] = localizeTaggedTemplates.map(tt => {
      return this.processTaggedTemplate(tt, idPrefix);
    });

    return processedTaggedTemplates;
  }

  private _extractLocalizeTaggedTemplates(node: ts.Node, localizeTaggedTemplates: LocalizeTaggedTemplateExtract[]) {

    const tts = ts;
    if (ts.isTaggedTemplateExpression(node)) {
      const tte = node as TaggedTemplateExpression;
      const tag = tte.tag;
      const template = tte.template;
      if (ts.isIdentifier(tag) && (ts.idText(tag) == '$localize' || ts.idText(tag) == '$translate')) {
        const literals: string[] = [];
        const raw: string[] = [];
        const expressions: string[] = [];
        if (ts.isNoSubstitutionTemplateLiteral(template)) {
          const nstl = template as NoSubstitutionTemplateLiteral;
          literals.push(nstl.text);
          raw.push(nstl.rawText);
        } else {
          const te = template as TemplateExpression;
          const templateHead = te.head;
          const templateSpans = te.templateSpans;
          literals.push(templateHead.text);
          raw.push(templateHead.rawText);

          templateSpans.forEach((span) => {
            const spanExpression = span.expression;
            const spanLiteral = span.literal;
            literals.push(spanLiteral.text);
            raw.push(spanLiteral.rawText);
            expressions.push(spanExpression.getText());

            if (ts.isTemplateMiddle(spanLiteral)) {
              const middle = spanLiteral as TemplateMiddle;
            } else {
              const tail = spanLiteral as TemplateTail;
            }
          });
        }
        localizeTaggedTemplates.push({literals: literals, raw: raw, expressions: expressions});
      }
    }
    node.forEachChild((n ) => this._extractLocalizeTaggedTemplates(n, localizeTaggedTemplates));
  }

  processTaggedTemplate(tt: LocalizeTaggedTemplateExtract, idPrefix: string) {
    let literals = ɵmakeTemplateObject(tt.literals, tt.raw);
    const literalLocations = tt.literals.map(x =>  {
      return {
        start: { line: 0, column: 0},
        end: { line: 0, column: 0},
        file: "",
        text: ""
      }
    }) as ɵSourceLocation[];
    const expressionLocations = tt.expressions.map(x =>  {
      return {
        start: { line: 0, column: 0},
        end: { line: 0, column: 0},
        file: "",
        text: ""
      }
    }) as ɵSourceLocation[];

    try {
      let parsedMessage: ɵParsedMessage = ɵparseMessage(literals, tt.expressions, null, literalLocations, expressionLocations);
      if (parsedMessage.customId) {
        if (idPrefix) {
          parsedMessage.customId = `${idPrefix}.${parsedMessage.customId}`;
        }
      }
      const expressions = Object.values(parsedMessage.substitutions);
      const text = parsedMessage.text.replace(/\{\$PH\}/, '{0}').replace(/\$PH_([0-9]+)/, "$1");
      return {
        id: parsedMessage.customId,
        meaning: parsedMessage.meaning,
        description: parsedMessage.description,
        literals: parsedMessage.messageParts,
        expressions: expressions,
        text: text
      };
    } catch (e) {
      let text = tt.literals[0];
      for (let i = 1; i < tt.literals.length; ++i) {
        text += tt.expressions[i - 1] + tt.literals[i];
      }
      console.error("processedTaggedTemplates", e, text);
      return {
        id: null,
        meaning: "",
        description: "",
        literals: tt.literals,
        expressions: tt.expressions,
        text: text
      }
    }
  }
}
